import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { shiftDayKey, calendarToday } from "../lib/days";
import { rosterLookbackDays } from "../lib/roster";
import { SET_DAY_SELECT, deriveRoster, rawSetOf, type RosterData } from "../lib/rosterData";
import { nextWeekPairs, readNextWeekChanges } from "../lib/nextWeekProposal";
import { mondayKey } from "../lib/weekLine";
import { wantsProposal } from "../lib/thisWeekBoard";
import { useReturnTick } from "../lib/useReturnTick";

/* THE FOUR READS BEHIND THE CLIENTS PAGE AND THE THIS WEEK BOARD — once, for both (W72 step 1).
 *
 * Everything is read through RLS as the signed-in coach — the SAME policies the phone meets. Measured
 * per identity on 2026-08-28: as this coach, `coach_clients` returns 2 of 2, `profiles` 3 of 14,
 * `workout_sessions` 118 of 162 and `set_logs` 349. `set_logs_coach_read` is the policy that makes the
 * training signal visible at all; without it this is a page of blanks that looks exactly like a roster
 * of people who quit.
 *
 * 🔴 `set_logs`, NOT `workout_sessions.completed_at`. That column says when somebody pressed FINISH — a
 * button unreachable until 2026-08-24 — and reported anybody who never pressed it as never having
 * trained (7 rows carry it against 52 `status='completed'`; one real client had zero of both and 44
 * logged sets). BOUNDED BY TIME (`rosterLookbackDays`), and every label is worded to survive the bound.
 *
 * 🔴 `kind` IS READ with the workout, because a TASK is a `coach_workouts` row with `kind = 'task'`
 * assigned through the same RPC: counting a daily wrist series as prescribed training makes a
 * diligent athlete look non-compliant. `workout_id` is read for the next-week proposal.
 */

export type RosterState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: RosterData };

export function useRosterData(reloadKey: number, now: Date): RosterState {
  const [state, setState] = useState<RosterState>({ status: "loading" });
  // QUEUE N2c fix 3: a client who trained while the tab was away shows when the coach comes back.
  const returned = useReturnTick();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      /* `created_at` on the LINK is the day this coach wrote the invitation — it exists for a pending
         row with no account behind it. */
      const links = await supabase
        .from("coach_clients")
        .select("id, client_id, status, invited_first_name, invited_last_name, invited_email, created_at, ended_at, invite_token, invite_token_expires_at");
      if (links.error) {
        if (!cancelled) setState({ status: "error", message: links.error.message });
        return;
      }
      const clientIds = links.data.map((l) => l.client_id).filter((id): id is string => id !== null);

      const [profiles, assignments, sets] = await Promise.all([
        clientIds.length > 0
          ? supabase.from("profiles")
              .select("id, first_name, last_name, created_at, main_goals, training_days_per_week, avatar_url")
              .in("id", clientIds)
          : Promise.resolve({ data: [], error: null } as const),
        supabase.from("coach_assignments")
          .select("client_id, scheduled_date, workout_id, root_workout_id, coach_workouts!workout_id(name, kind, task_optional)"),
        clientIds.length > 0
          ? supabase
              .from("set_logs")
              // W112: the set AND its session's scheduled day — the day it counts on (`rosterData.rawSetOf`).
              .select(SET_DAY_SELECT)
              .in("user_id", clientIds)
              .not("completed_at", "is", null)
              .gte("completed_at", shiftDayKey(new Date(), -rosterLookbackDays))
          : Promise.resolve({ data: [], error: null } as const),
      ]);
      const firstError = profiles.error ?? assignments.error ?? sets.error;
      if (firstError) {
        if (!cancelled) setState({ status: "error", message: firstError.message });
        return;
      }
      const data = deriveRoster({
        links: links.data,
        profiles: profiles.data ?? [],
        // ⚠️ A PostgREST embed comes back as OBJECT or ARRAY by inferred relationship — the codex's own
        // trap — and `embeddedWorkout` reads both, so the row is passed through as it came.
        assignments: (assignments.data ?? []) as Parameters<typeof deriveRoster>[0]["assignments"],
        sets: ((sets.data ?? []) as Parameters<typeof rawSetOf>[0][]).map(rawSetOf),
      }, now);
      if (!cancelled) setState({ status: "ready", data });
    })();
    return () => { cancelled = true; };
  }, [reloadKey, now, returned]);

  return state;
}

/**
 * "N changes proposed", per link — read LAZILY, after the board is on screen (Fable's ruling d).
 * Only rows `wantsProposal` admits are read (an active client whose next week exists), each next-week
 * assignment against the previous copy of its own template (`nextWeekPairs` / `readNextWeekChanges`). A row stays absent until its count
 * lands, and a failed read stays absent too: no sentence is better than a wrong number.
 */
export function useProposals(data: RosterData | null): Record<string, number> {
  const [counts, setCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    if (data === null) return;
    let cancelled = false;
    for (const r of data.rows) {
      if (!wantsProposal({ status: r.status, firstAfterWeekISO: data.firstAfterWeekISO[r.linkId] ?? null })) continue;
      // Paired by TEMPLATE against this week's end (`nextWeekPairs` says why "latest vs previous" lied).
      const sunday = shiftDayKey(new Date(`${mondayKey(calendarToday(), 0)}T00:00:00Z`), 6);
      const pairs = nextWeekPairs(data.trainingByLink[r.linkId] ?? [], sunday);
      if (pairs.length === 0) continue;
      void readNextWeekChanges((fn, args) => supabase.rpc(fn as never, args as never), pairs).then((n) => {
        if (cancelled || n === null) return;
        setCounts((prev) => ({ ...prev, [r.linkId]: n }));
      });
    }
    return () => { cancelled = true; };
  }, [data]);
  return counts;
}
